/**
 * Superadmin-mode toggle state (#429 Wave 2).
 *
 * Two-factor escalation pattern (NIST AC-6(2)): a superadmin user
 * carries `isSuperadmin: true` on their session forever, but the
 * server-side `requireSuperadmin` middleware (the ONLY gate that
 * honors superadmin elevation today) only fires when the request
 * also carries the `x-superadmin-mode: on` header. This module owns
 * the toggle state that decides whether to send that header.
 *
 * Scope note: the elevation is admin-tier only. `requirePermission`
 * and `requireOrgPermission` deliberately do NOT honor the header —
 * cross-org *workspace data* access uses Better Auth impersonation
 * instead, not header-based escalation. See PR #519 review and
 * `apps/server/src/infrastructure/auth/middleware.ts` for the locked
 * design.
 *
 * Storage: sessionStorage — per-tab, cleared on tab close. Combined
 * with the explicit `clearSuperadminMode()` calls in
 * `Header.signOut` and on `IdentityProvider` auth transitions, the
 * toggle defaults OFF on every new session. That's the persistence
 * choice locked in the Wave 2 design discussion: per-session
 * (clears on logout) — more deliberate than localStorage (which
 * would make escalation a one-time decision), more ergonomic than
 * per-tab sessionStorage that doesn't survive a tab refresh within
 * the same session.
 *
 * React reactivity: components subscribe via `useSuperadminMode()`
 * (in `hooks/useSuperadminMode.ts`), which wraps the listener API
 * here with `useSyncExternalStore` — the React-18-supported way to
 * subscribe to external mutable state. The `apiFetch` helper reads
 * the current value synchronously (no subscription needed) when
 * attaching the header.
 */
const STORAGE_KEY = 'ottotester.superadminMode'

type Listener = () => void
const listeners = new Set<Listener>()

function readStorage(): boolean {
  // sessionStorage is unavailable in SSR / certain test harnesses —
  // tolerate both by defaulting OFF. Reading the canonical 'on' value
  // (the same string the header carries) keeps the storage state and
  // the wire format aligned.
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'on'
  } catch {
    return false
  }
}

function writeStorage(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(STORAGE_KEY, 'on')
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Fail-closed: there is no in-memory fallback. The read path
    // (`readStorage`) also tolerates storage failure by returning
    // false, so a session where sessionStorage is unavailable
    // simply CAN'T enter superadmin mode at all — the toggle stays
    // off no matter how many times the user clicks. Preferred over
    // a parallel in-memory state that would silently diverge from
    // what other tabs / future reads see.
  }
}

/** Current toggle state. Synchronous; safe to call from anywhere
 * (including outside React, e.g. from `apiFetch` when attaching the
 * header to a request). */
export function getSuperadminMode(): boolean {
  return readStorage()
}

/** Flip the toggle and notify subscribers. Components that have
 * subscribed via `useSuperadminMode()` re-render on the next tick. */
export function setSuperadminMode(on: boolean): void {
  writeStorage(on)
  for (const l of listeners) l()
}

/** Force-clear the toggle. Called from the signOut path so a new
 * session always lands with the toggle OFF — the escalation must be a
 * conscious decision in each session. */
export function clearSuperadminMode(): void {
  setSuperadminMode(false)
}

/** Subscribe to toggle changes. Returns an unsubscribe function.
 * `useSuperadminMode()` uses this with `useSyncExternalStore`; direct
 * callers (e.g. tests) can use it too. */
export function subscribeSuperadminMode(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Header name the backend reads in `isSuperadminEscalation()`.
 * Exported as a constant so the frontend wire-format stays in sync
 * with the backend's expected name without a string literal in
 * `apiFetch`. */
export const SUPERADMIN_HEADER = 'x-superadmin-mode'

/** Header value the backend accepts. Exporting the literal next to the
 * header name so a future "off"/"true"/etc. shift is one change. */
export const SUPERADMIN_HEADER_ON = 'on'
